import React from "react";
import useApi from "@/hooks/useApi";

/**
 * Velaro internal demo page: /admin/mf-athletic-demo
 * Site 1032 only. Shows web chat + IVR demo config for MF Athletic (BigCommerce bot).
 * NOT visible to customers — direct URL access only. Route-level gating handles auth.
 */

const EMBED_CODE = `<script>
  window.__velaro = { siteId: "1032", mode: "demo" };
</script>
<script src="https://cdn-us-east-test-velaro.azurewebsites.net/widget.js" async></script>`.trim();

interface IntegrationSource {
  sourceType: string;
  documentCount: number;
  totalChunks: number;
  lastSyncedAt?: string;
}

// --- Vector Index Status panel ---

function VectorIndexStatus() {
  const api = useApi();
  const [sources, setSources] = React.useState<IntegrationSource[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchSources = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.messaging.get("AzureIndexes/IntegrationSources");
      if (resp.ok) {
        const data = await resp.json();
        setSources(Array.isArray(data?.sources) ? data.sources : []);
      } else {
        setError("API returned an error — check that you are logged in and velaro-messaging is running.");
      }
    } catch {
      setError("Unable to load vector index data.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => { fetchSources(); }, [fetchSources]);

  const scraperSource = sources.find((s) => s.sourceType === "scraper_pages");
  const bcSource = sources.find((s) => s.sourceType === "bigcommerce");

  return (
    <div className="mt-1">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
          <i className="fas fa-circle-notch fa-spin text-blue-500" aria-hidden="true" />
          Loading index stats…
        </div>
      )}
      {!loading && error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <i className="fas fa-triangle-exclamation mr-1.5" aria-hidden="true" />
          {error}
        </div>
      )}
      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Scraper pages */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                <i className="fas fa-spider text-indigo-600 text-xs" aria-hidden="true" />
              </span>
              <span className="text-sm font-medium text-slate-700">Scraper Pages</span>
            </div>
            {scraperSource ? (
              <>
                <p className="text-2xl font-bold text-slate-800 mt-1">{scraperSource.documentCount.toLocaleString()}</p>
                <p className="text-xs text-slate-400 mt-0.5">{scraperSource.totalChunks.toLocaleString()} chunks indexed</p>
                {scraperSource.lastSyncedAt && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Last sync: {new Date(scraperSource.lastSyncedAt).toLocaleString()}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-400 mt-1">No scraper data indexed yet</p>
            )}
          </div>

          {/* BigCommerce products */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-700 font-bold text-xs">BC</span>
              </span>
              <span className="text-sm font-medium text-slate-700">BigCommerce Products</span>
            </div>
            {bcSource ? (
              <>
                <p className="text-2xl font-bold text-slate-800 mt-1">{bcSource.documentCount.toLocaleString()}</p>
                <p className="text-xs text-slate-400 mt-0.5">{bcSource.totalChunks.toLocaleString()} chunks indexed</p>
                {bcSource.lastSyncedAt && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Last sync: {new Date(bcSource.lastSyncedAt).toLocaleString()}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-400 mt-1">No BC product data indexed yet</p>
            )}
          </div>

          {/* All sources summary */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                <i className="fas fa-database text-green-600 text-xs" aria-hidden="true" />
              </span>
              <span className="text-sm font-medium text-slate-700">All Sources</span>
            </div>
            <p className="text-2xl font-bold text-slate-800 mt-1">
              {sources.reduce((acc, s) => acc + s.documentCount, 0).toLocaleString()}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {sources.reduce((acc, s) => acc + s.totalChunks, 0).toLocaleString()} total chunks
            </p>
            <p className="text-xs text-slate-400 mt-0.5">{sources.length} source type{sources.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
      )}

      {!loading && (
        <button
          type="button"
          onClick={fetchSources}
          className="mt-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
        >
          <i className="fas fa-rotate-right mr-1" aria-hidden="true" />
          Refresh
        </button>
      )}
    </div>
  );
}

// --- Ingest Scraped Data panel ---

interface ScrapedPage { url: string; title: string; text: string; text_length: number; }
interface ScrapedExport {
  export_metadata: { job_id: string; domain: string; pages_scraped: number };
  data: ScrapedPage[];
}

function toContentId(url: string): string {
  const slug = url.replace("https://www.everythingtrackandfield.com", "").replace(/^\//, "") || "homepage";
  return "etf_" + slug.replace(/[/\\?#&=.\-]+/g, "_").replace(/_+/g, "_").replace(/_$/, "");
}

function IngestScrapedData() {
  const api = useApi();
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = React.useState<ScrapedExport | null>(null);
  const [done, setDone] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [counts, setCounts] = React.useState({ ingested: 0, unchanged: 0, errors: 0 });
  const [running, setRunning] = React.useState(false);
  const [finished, setFinished] = React.useState(false);
  const [log, setLog] = React.useState<string[]>([]);
  const abortRef = React.useRef(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        setParsed(JSON.parse(ev.target?.result as string));
        setFinished(false);
        setLog([]);
      } catch { alert("Could not parse JSON."); }
    };
    reader.readAsText(f);
  };

  const runIngest = async () => {
    if (!parsed) return;
    abortRef.current = false;
    const pages = parsed.data.filter((p) => p.text_length >= 200);
    setTotal(pages.length);
    setDone(0);
    setCounts({ ingested: 0, unchanged: 0, errors: 0 });
    setLog([]);
    setRunning(true);
    setFinished(false);
    let ing = 0, unc = 0, err = 0;
    const newLog: string[] = [];
    for (let i = 0; i < pages.length; i++) {
      if (abortRef.current) { newLog.push("⚠ Aborted."); break; }
      const p = pages[i];
      const text = p.text.length > 40000 ? p.text.slice(0, 40000) : p.text;
      try {
        const resp = await api.messaging.post("AzureIndexes/IngestContent", {
          contentId: toContentId(p.url), title: p.title || toContentId(p.url), text, sourceUrl: p.url
        });
        if (resp.ok) {
          const d = await resp.json();
          if (d.unchanged) { unc++; newLog.push(`[${i+1}/${pages.length}] ${p.title} — unchanged`); }
          else { ing++; newLog.push(`[${i+1}/${pages.length}] ${p.title} — ${d.chunks ?? "?"} chunks`); }
        } else { err++; newLog.push(`[${i+1}/${pages.length}] ${p.title} — HTTP ${resp.status}`); }
      } catch { err++; newLog.push(`[${i+1}/${pages.length}] ${p.title} — network error`); }
      setCounts({ ingested: ing, unchanged: unc, errors: err });
      setDone(i + 1);
      setLog([...newLog]);
    }
    setRunning(false);
    setFinished(true);
  };

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const qualifying = parsed ? parsed.data.filter((p) => p.text_length >= 200).length : 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <span className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
          <i className="fas fa-upload text-white text-sm" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-slate-800">Ingest Scraped Data</h2>
          <p className="text-xs text-slate-400">Upload the velaro-scraper JSON export to index it into site 1032's vector index</p>
        </div>
      </div>
      <div className="px-5 py-4 space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">Scraped JSON Export</label>
          <input ref={fileRef} type="file" accept=".json" onChange={handleFile}
            className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100" />
          <p className="text-xs text-slate-400 mt-1">
            File: <code className="font-mono bg-slate-100 px-1 rounded">everythingtrackandfield.com-2026-03-17-5399026b.json</code>
          </p>
        </div>

        {parsed && !running && !finished && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
            <p className="text-sm font-semibold text-emerald-800 mb-1">
              <i className="fas fa-circle-check mr-1.5" aria-hidden="true" />
              Ready: {parsed.export_metadata.domain} — {qualifying} pages to ingest
            </p>
            <button type="button" onClick={runIngest}
              className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors">
              <i className="fas fa-rocket text-xs" aria-hidden="true" />
              Start Ingest ({qualifying} pages)
            </button>
          </div>
        )}

        {(running || finished) && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>{done} / {total} pages</span><span>{pct}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full transition-all duration-200" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex gap-4 text-xs mt-1.5">
                <span className="text-emerald-600">✓ {counts.ingested} new</span>
                <span className="text-slate-400">= {counts.unchanged} unchanged</span>
                {counts.errors > 0 && <span className="text-red-500">✗ {counts.errors} errors</span>}
              </div>
            </div>
            {running && (
              <button type="button" onClick={() => { abortRef.current = true; }}
                className="text-xs text-red-500 hover:text-red-700">
                <i className="fas fa-stop mr-1" aria-hidden="true" />Abort
              </button>
            )}
            {finished && (
              <div className="text-sm text-emerald-700 font-medium">
                <i className="fas fa-flag-checkered mr-1.5" aria-hidden="true" />
                Done — {counts.ingested} ingested, {counts.unchanged} unchanged{counts.errors > 0 ? `, ${counts.errors} errors` : ""}
              </div>
            )}
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Log ({log.length} entries)</summary>
              <pre className="mt-2 bg-slate-900 text-green-400 rounded-lg px-3 py-2 overflow-y-auto max-h-48 font-mono text-xs whitespace-pre-wrap">{log.join("\n")}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Coach Sarah Demo Script panel ---

const DEMO_STAGES = [
  {
    id: 1,
    label: "Stage 1 — FAQ",
    color: "blue",
    status: "partial" as const,
    statusNote: "Works once ETF data is ingested (use Ingest panel below)",
    prompts: [
      "What's your return policy on equipment?",
      "Do you offer free shipping on orders over $100?",
      "What's the warranty on pole vault poles?",
      "How long does shipping take?",
    ],
    talkingPoints: [
      "Instant response — no agent, no wait time",
      "Consistent answer every time — no human error",
      "Available 24/7 even when the team is offline",
      "This is the 80% of tickets her Gorgias agents answer manually every day",
    ],
  },
  {
    id: 2,
    label: "Stage 2 — Order Lookup",
    color: "green",
    status: "ready" as const,
    statusNote: "Live today — connected to Shoeman BigCommerce store",
    prompts: [
      "Where is my order?",
      "Look up order 100",
      "Find my orders for coach@school.edu",
      "What's the status of my most recent order?",
    ],
    talkingPoints: [
      "Bot pulls live data directly from BigCommerce — this is not a generic chatbot",
      "Customer gets a real answer, not 'please contact support'",
      "Eliminates a massive chunk of inbound order-status tickets",
    ],
  },
  {
    id: 3,
    label: "Stage 3 — RMA Initiation",
    color: "orange",
    status: "needs-build" as const,
    statusNote: "PR ready — needs: RMA skill + escalation node in workflow",
    prompts: [
      "I need to return a damaged item",
      "My pole vault pole arrived broken, I need to return it",
      "I want to start a return on order 100",
    ],
    talkingPoints: [
      "Bot identifies the correct order, confirms item with Sarah, initiates RMA automatically",
      "Routes to Customer Service — not Sales — with RMA already started",
      "Agent receives conversation with context already built — Sarah never repeats herself",
      "This is the routing between groups Tracy specifically asked for",
    ],
  },
  {
    id: 4,
    label: "Stage 4 — Agent Workspace",
    color: "purple",
    status: "partial" as const,
    statusNote: "BC panel exists (read-only) — write-back + SMS need PR",
    prompts: [
      "(Agent picks up the conversation after RMA escalation)",
      "Agent approves the return directly from panel",
      "Agent updates order status in BigCommerce from chat",
    ],
    talkingPoints: [
      "Agent sees full order history across both stores immediately",
      "Open RMA with reason already filled — no asking Sarah to repeat herself",
      "No switching between tabs, no copy-pasting order numbers",
      "This is the Gorgias comparison moment — night and day difference",
    ],
  },
  {
    id: 5,
    label: "Stage 5 — Cross-Store Visibility",
    color: "red",
    status: "needs-build" as const,
    statusNote: "Needs: second BC config (Perform Better) + cross-store skill",
    prompts: [
      "Also — I placed an order on Perform Better last week, any update?",
    ],
    talkingPoints: [
      "Agent sees it immediately in the same panel — different storefront, same customer record",
      "One agent, one conversation, two storefronts — completely seamless",
      "This is the exact multi-store problem Tracy described as her biggest challenge",
      "This is something Gorgias cannot do cleanly for a BigCommerce multi-storefront setup",
    ],
  },
];

const STATUS_STYLES = {
  ready:        { bg: "bg-green-50",  border: "border-green-200",  dot: "bg-green-500",  text: "text-green-800",  label: "READY" },
  partial:      { bg: "bg-amber-50",  border: "border-amber-200",  dot: "bg-amber-500",  text: "text-amber-800",  label: "PARTIAL" },
  "needs-build":{ bg: "bg-red-50",    border: "border-red-200",    dot: "bg-red-500",    text: "text-red-800",    label: "NEEDS BUILD" },
};

const STAGE_COLORS: Record<string, string> = {
  blue:   "bg-blue-600",
  green:  "bg-green-600",
  orange: "bg-orange-500",
  purple: "bg-purple-600",
  red:    "bg-red-600",
};

function DemoScript() {
  const [openStage, setOpenStage] = React.useState<number | null>(1);
  const [copied, setCopied] = React.useState<string | null>(null);

  const copyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(prompt);
      setTimeout(() => setCopied(null), 1500);
    }).catch(() => {});
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50">
        <span className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
          <i className="fas fa-person-chalkboard text-white text-sm" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-slate-800">Coach Sarah — Demo Script</h2>
          <p className="text-xs text-slate-400">Step-by-step walkthrough for the MF Athletic demo. Click a stage to expand.</p>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {DEMO_STAGES.map((stage) => {
          const s = STATUS_STYLES[stage.status];
          const isOpen = openStage === stage.id;
          return (
            <div key={stage.id}>
              <button
                type="button"
                onClick={() => setOpenStage(isOpen ? null : stage.id)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left"
              >
                <span className={`w-7 h-7 rounded-full ${STAGE_COLORS[stage.color]} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>
                  {stage.id}
                </span>
                <span className="font-medium text-slate-700 flex-1">{stage.label}</span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${s.bg} ${s.border} ${s.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                  {s.label}
                </span>
                <i className={`fas fa-chevron-${isOpen ? "up" : "down"} text-slate-400 text-xs ml-1`} aria-hidden="true" />
              </button>

              {isOpen && (
                <div className="px-5 pb-4 space-y-4">
                  {/* Status note */}
                  <div className={`text-xs px-3 py-2 rounded-lg border ${s.bg} ${s.border} ${s.text}`}>
                    <i className="fas fa-circle-info mr-1" aria-hidden="true" />
                    {stage.statusNote}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Prompts to test */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        <i className="fas fa-comment-dots mr-1" aria-hidden="true" />
                        Test Prompts — say these in the chat widget
                      </p>
                      <div className="space-y-1.5">
                        {stage.prompts.map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => copyPrompt(p)}
                            className="w-full text-left px-3 py-2 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-lg text-sm text-slate-700 transition-colors group"
                          >
                            <span className="flex items-start gap-2">
                              <i className={`fas ${copied === p ? "fa-check text-green-500" : "fa-copy text-slate-400 group-hover:text-blue-500"} text-xs mt-0.5 flex-shrink-0`} aria-hidden="true" />
                              <span className="italic">"{p}"</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Talking points */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        <i className="fas fa-bullhorn mr-1" aria-hidden="true" />
                        Talking Points for Tracy
                      </p>
                      <ul className="space-y-1.5">
                        {stage.talkingPoints.map((pt) => (
                          <li key={pt} className="flex items-start gap-2 text-sm text-slate-600">
                            <i className="fas fa-circle-check text-green-500 text-xs mt-0.5 flex-shrink-0" aria-hidden="true" />
                            {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- What's needed to complete the story (build checklist) ---

const GAPS = [
  {
    item: "ETF/MF Athletic knowledge ingested into vector index",
    where: "Use the Ingest panel below — pick the JSON file, click Start",
    effort: "10 min",
    status: "pending" as const,
  },
  {
    item: "Update bot greeting from 'Shoeman' → 'MF Athletic'",
    where: "AI Chatbot → Chatbot Settings → AI Config 113 → edit prompt",
    effort: "5 min",
    status: "pending" as const,
  },
  {
    item: "RMA initiation skill (bigcommerce_create_return)",
    where: "velaro-messaging — new BigCommerceCreateReturnSkill.cs → companion PR ready",
    effort: "In PR",
    status: "pr" as const,
  },
  {
    item: "Escalation path in alexflowbigcommerce: bot → Customer Service team",
    where: "Workflows → alexflowbigcommerce → add transferToAgents node after RMA",
    effort: "In PR",
    status: "pr" as const,
  },
  {
    item: "Pre-fill RMA context on handoff (order ID, item, reason)",
    where: "Workflow: setData nodes before transferToAgents",
    effort: "In PR",
    status: "pr" as const,
  },
  {
    item: "Agent write-back: update order status from BC panel",
    where: "velaro-messaging — BigCommercePanel Orders tab → Update Status button",
    effort: "In PR",
    status: "pr" as const,
  },
  {
    item: "SMS confirmation after agent approves RMA",
    where: "velaro-messaging — new sendSms workflow action + trigger on RMA approved",
    effort: "In PR",
    status: "pr" as const,
  },
  {
    item: "Second BC store config: Perform Better",
    where: "Integrations → BigCommerce → Add second store connection",
    effort: "Config",
    status: "pending" as const,
  },
  {
    item: "Cross-store customer lookup (MF Athletic + Perform Better in one panel)",
    where: "velaro-messaging — BigCommerceCrossStoreSearchSkill.cs + agent panel update",
    effort: "In PR",
    status: "pr" as const,
  },
];

const GAP_STYLES = {
  pending: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", label: "TODO" },
  pr:      { bg: "bg-blue-50",  border: "border-blue-200",  text: "text-blue-700",  label: "IN PR" },
  done:    { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", label: "DONE" },
};

function BuildChecklist() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50">
        <span className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
          <i className="fas fa-list-check text-white text-sm" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-slate-800">Full Demo Story — Build Checklist</h2>
          <p className="text-xs text-slate-400">Everything needed to run the complete 5-stage Coach Sarah story</p>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {GAPS.map((g) => {
          const s = GAP_STYLES[g.status];
          return (
            <div key={g.item} className="flex items-start gap-3 px-5 py-3">
              <span className={`inline-flex items-center text-xs font-bold px-2 py-0.5 rounded border flex-shrink-0 mt-0.5 ${s.bg} ${s.border} ${s.text}`}>
                {s.label}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700">{g.item}</p>
                <p className="text-xs text-slate-400 mt-0.5">{g.where}</p>
              </div>
              <span className="text-xs text-slate-400 flex-shrink-0">{g.effort}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Main page ---

export default function MFAthleticDemo() {
  const [copied, setCopied] = React.useState(false);

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(EMBED_CODE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      /* clipboard not available — silent fail */
    });
  };

  const shareableUrl = `${window.location.origin}${window.location.pathname}?trigger=mf-athletic-demo`;

  return (
    <div className="p-6 max-w-5xl">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
            <i className="fas fa-running text-orange-600" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-bold text-slate-800">MF Athletic — Everything Track &amp; Field Demo</h1>
        </div>
        <p className="text-slate-500 text-sm ml-12">
          Velaro-internal demo page for site 1032. Shows web chat widget + IVR demo configuration for
          the MF Athletic BigCommerce bot workflow.
        </p>
      </div>

      {/* Two-column demo panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">

        {/* Web Chat Demo panel */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-blue-50">
            <span className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
              <i className="fas fa-comments text-white text-sm" aria-hidden="true" />
            </span>
            <h2 className="text-base font-semibold text-slate-800">Web Chat Demo</h2>
          </div>

          <div className="px-5 py-4 space-y-4">
            <p className="text-sm text-slate-600">
              This widget connects to <strong>site 1032</strong>'s BigCommerce bot workflow.
              Embed the script below on any staging page to trigger the demo chat experience.
            </p>

            {/* Embed code block */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Embed Code</span>
                <button
                  type="button"
                  onClick={copyEmbedCode}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                    copied
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <i className={`fas ${copied ? "fa-check" : "fa-copy"} text-xs`} aria-hidden="true" />
                  {copied ? "Copied!" : "Copy Embed Code"}
                </button>
              </div>
              <pre className="bg-slate-900 text-green-400 rounded-lg px-4 py-3 text-xs overflow-x-auto font-mono whitespace-pre">
                {EMBED_CODE}
              </pre>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <a
                href="https://admin-staging.velaro.com/demo/mf-athletic-preview.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <i className="fas fa-external-link-alt text-xs" aria-hidden="true" />
                Open Live Demo
              </a>
            </div>

            {/* Shareable URL */}
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Shareable Demo Link</p>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <i className="fas fa-link text-slate-400 text-xs flex-shrink-0" aria-hidden="true" />
                <span className="text-xs text-slate-600 font-mono break-all">{shareableUrl}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Append <code className="font-mono bg-slate-100 px-1 rounded">?trigger=mf-athletic-demo</code> to any admin page URL.
              </p>
            </div>
          </div>
        </div>

        {/* IVR Demo panel */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-purple-50">
            <span className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
              <i className="fas fa-phone text-white text-sm" aria-hidden="true" />
            </span>
            <h2 className="text-base font-semibold text-slate-800">IVR Demo</h2>
          </div>

          <div className="px-5 py-4 space-y-4">
            {/* Phone number display */}
            <div className="text-center py-4 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
              <div>
                <p className="text-2xl font-bold text-slate-800 tracking-wide font-mono">+1 (805) 918-1607</p>
                <p className="text-xs text-slate-500 mt-0.5">Rachel — MF Athletic Demo - IVR</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 tracking-wide font-mono">+1 (323) 214-3752</p>
                <p className="text-xs text-slate-500 mt-0.5">Adam — MF Athletic Demo - IVR</p>
              </div>
              <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-0.5 inline-block">
                <i className="fas fa-circle-check mr-1" aria-hidden="true" />
                Both numbers assigned to alexflowbigcommerce
              </p>
            </div>

            {/* Call instructions */}
            <div className="flex items-start gap-2.5 text-sm text-slate-600">
              <i className="fas fa-circle-info text-blue-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p>Call this number to demo the <strong>MF Athletic voice assistant</strong> powered by the BigCommerce AI workflow.</p>
            </div>

            {/* IVR workflow label */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">IVR Workflow</span>
                <span className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  <i className="fas fa-sitemap text-xs" aria-hidden="true" />
                  alexworkflow (BigCommerce)
                </span>
              </div>
            </div>

            {/* View IVR Config link */}
            <a
              href="/ivr"
              className="inline-flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors"
            >
              <i className="fas fa-gear text-xs" aria-hidden="true" />
              View IVR Config
            </a>
          </div>
        </div>
      </div>

      {/* Routing Rules section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
              <i className="fas fa-route text-white text-sm" aria-hidden="true" />
            </span>
            <h2 className="text-base font-semibold text-slate-800">How This Demo Is Isolated</h2>
          </div>
          <a
            href="/rules"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            Go to Rules Manager
            <i className="fas fa-arrow-right text-xs" aria-hidden="true" />
          </a>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <i className="fas fa-circle-info text-blue-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-blue-800">
                These routing rules ensure the MF Athletic demo runs in isolation. Standard site 1032 traffic is
                unaffected — only sessions that explicitly identify as demo traffic are routed to the demo workflow.
              </p>
            </div>
          </div>

          {/* Rule 1 */}
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                1
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-700">Highest Priority — Attribute Match</p>
                <p className="text-sm text-slate-600 mt-0.5">
                  When conversation opens with attribute{" "}
                  <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">demo_mode = mf_athletic</code>
                  {" "}→ Use workflow:{" "}
                  <span className="font-semibold text-purple-700">alexworkflow</span>
                </p>
              </div>
            </div>
          </div>

          {/* Rule 2 */}
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                2
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-700">URL Contains Match</p>
                <p className="text-sm text-slate-600 mt-0.5">
                  When URL contains{" "}
                  <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">mf-athletic-demo</code>
                  {" "}→ Use workflow:{" "}
                  <span className="font-semibold text-purple-700">alexworkflow</span>
                </p>
              </div>
            </div>
          </div>

          {/* Isolation note */}
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <i className="fas fa-shield-halved text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-green-800">
                <strong>All other web traffic uses default routing</strong> — this demo does NOT affect production traffic.
                The demo workflow is only triggered by the explicit attribute or URL pattern above.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Coach Sarah demo script */}
      <DemoScript />

      {/* Build checklist */}
      <BuildChecklist />

      {/* Ingest Scraped Data section */}
      <IngestScrapedData />

      {/* Vector Index Status section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <span className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
            <i className="fas fa-magnifying-glass-chart text-white text-sm" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Vector Index Status</h2>
            <p className="text-xs text-slate-400">Site 1032 — live data from AzureIndexes/IntegrationSources</p>
          </div>
        </div>

        <div className="px-5 py-4">
          <VectorIndexStatus />
        </div>
      </div>
    </div>
  );
}
